/**
 * 日報/サマリ の入力(日別のポップアップ。2026-09-10 指示: 月の一覧は閲覧だけにし、各日付の右の「入力」から開く)。
 * その日の会社ごとに 出勤形態×報酬区分 の 取得件数・納品件数・人件費・交通費 を入力し、その日だけ保存する。
 * 取得件数・人件費・交通費の初期値は日次集計からの自動計算。自動値と違う値は赤字、「戻す」で自動値に戻す。
 * 未保存のまま閉じようとしたら確認する。
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import Modal from '@core/components/Modal';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { weekdayLabel } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { COST_STYLE_LABELS, priceOn, type Company, type DateStr, type Delivery, type DeliveryDetail } from '@core/lib/types';
import { saveDelivery } from './deliveryRepo';
import { buildDetail, cloneCompanyEdits, deliveryKeyOf, emptyCompanyEdit, stylesFor, sumCompany, val, type CompanyEdit, type CompanyInfo } from './deliveryEdit';

// 文字サイズはクラス(.delivery-num)で指定する。インラインで指定するとスマホの 16px(iPhone の拡大防止)に勝ってしまうため(2026-10-05)
const inputStyle = { width: 84, padding: '2px 6px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', textAlign: 'right' } as const;

export default function DeliveryDayModal({
  date,
  companies,
  info,
  initial,
  before,
  onClose,
  onSaved,
  onDirtyChange,
}: {
  date: DateStr;
  /** 稼働中の会社(会社マスタ) */
  companies: Company[];
  /** その日の自動計算(日次集計)。無い日は undefined */
  info: Record<string, CompanyInfo> | undefined;
  /** 入力欄の初期値(会社ID → 入力値) */
  initial: Record<string, CompanyEdit>;
  /** 保存済みの deliveries(無ければ null) */
  before: Delivery | null;
  onClose: () => void;
  onSaved: () => void;
  /** 未保存の変更の有無(親の離脱確認に使う) */
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [edit, setEdit] = useState<Record<string, CompanyEdit>>(() => cloneCompanyEdits(initial));
  const [dirty, setDirty] = useState(false);
  /** 既定は **全社・全区分**(2026-09-22 発注者指示「入力画面は全社を表示」。D-125)。ボタンで「稼働している会社のみ」に絞れる */
  const [showAll, setShowAll] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const setCompany = (cid: string, f: (e: CompanyEdit) => CompanyEdit) => {
    setEdit((prev) => ({ ...prev, [cid]: f(prev[cid] ?? emptyCompanyEdit()) }));
    setDirty(true);
  };

  /** 表示する会社。既定は全社。「稼働している会社のみ」にすると、その日に稼働(日次集計か保存済み内訳)のある会社だけ */
  const list = companies.filter((c) => showAll || info?.[c.id] || before?.details?.[c.id] || before?.counts?.[c.id] !== undefined);
  const dateLabel = `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}${weekdayLabel(date)}`;
  const total = { appointments: 0, delivered: 0, sales: 0, labor: 0, commute: 0 };
  for (const c of list) {
    const s = sumCompany(edit[c.id] ?? emptyCompanyEdit());
    total.appointments += s.appointments;
    total.delivered += s.delivered;
    total.sales += s.delivered * priceOn(c, date);
    total.labor += s.labor;
    total.commute += s.commute;
  }

  async function save() {
    if (!user) return;
    setBusy(true);
    try {
      const details: Record<string, DeliveryDetail> = {};
      for (const c of companies) {
        const e = edit[c.id];
        if (!e) continue;
        const d = buildDetail(dateLabel, c.name, e, info?.[c.id]);
        if (d) details[c.id] = d;
      }
      await saveDelivery(date, details, before?.note ?? '', user.id, before);
      toast(`${dateLabel} を保存しました`);
      setDirty(false);
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const requestClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };

  const input = (value: string, onChange: (v: string) => void, placeholder?: string, edited = false) => (
    <NumInput className={edited ? 'delivery-num edited' : 'delivery-num'} value={value} placeholder={placeholder} onChange={onChange} style={inputStyle} disabled={busy} />
  );
  const resetLink = (title: string, onReset: () => void) => (
    <button type="button" className="txn-link" title={title} style={{ fontSize: 11, padding: '0 3px' }} onClick={onReset}>
      戻す
    </button>
  );

  return (
    <Modal onClose={requestClose} width="min(1100px, 96vw)">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        日報/サマリ 入力
        <span className="day-label">
          {date.slice(0, 4)}/{dateLabel}
        </span>
        <span className="hint" style={{ fontWeight: 400 }}>
          納品 {total.delivered}件(売上 ¥{formatYen(total.sales)}) ／ 人件費 ¥{formatYen(total.labor)} ／ 交通費 ¥{formatYen(total.commute)}
        </span>
        <button type="button" className={`chip${showAll ? ' on' : ''}`} style={{ marginLeft: 'auto' }} onClick={() => setShowAll((v) => !v)}>
          {showAll ? '全社を表示中(押すと稼働している会社のみ)' : '稼働している会社のみ(押すと全社)'}
        </button>
      </h3>
      <p className="hint" style={{ margin: '0 0 10px' }}>取得件数・人件費・交通費の初期値はこの日の自動計算(日次集計)。自動値と違う値は赤字で、「戻す」で自動値に戻ります。</p>
      <div className="table-scroll">
        <table className="fit-table tree-table fixed-cols">
          <colgroup>
            <col />
            <col style={{ width: 90 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 150 }} />
          </colgroup>
          <thead>
            <tr>
              <th>会社 ／ 出勤形態×報酬区分</th>
              <th className="num">会社単価</th>
              <th className="num">取得件数</th>
              <th className="num">納品件数</th>
              <th className="num">人件費(円)</th>
              <th className="num">交通費(円)</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="hint">
                  この日に稼働している会社がありません(「稼働している会社のみ」を押すと全社・全区分に入力できます)
                </td>
              </tr>
            )}
            {list.flatMap((c) => {
              const e = edit[c.id] ?? emptyCompanyEdit();
              const ci = info?.[c.id];
              const s = sumCompany(e);
              const unit = priceOn(c, date);
              const rows = [
                <tr key={c.id} className="tree-l1" style={{ fontWeight: 700 }}>
                  <td>{c.name}</td>
                  <td className="num">{formatYen(unit)}</td>
                  <td className="num">{s.appointments || ''}</td>
                  <td className="num">{s.delivered || ''}</td>
                  <td className="num">{s.labor ? `¥${formatYen(s.labor)}` : ''}</td>
                  <td className="num">{s.commute ? `¥${formatYen(s.commute)}` : ''}</td>
                </tr>,
              ];
              for (const k of stylesFor(ci, e, showAll)) {
                const dk = deliveryKeyOf(k);
                const autoL = ci?.labor[k] ?? 0;
                const autoC = ci?.commute[k] ?? 0;
                const diffL = e.labor[k].trim() === '' ? null : val(e.labor[k]) - autoL;
                const diffC = e.commute[k].trim() === '' ? null : val(e.commute[k]) - autoC;
                const autoA = dk ? (ci?.appts[dk] ?? 0) : 0;
                const curA = dk ? e.appointments[dk].trim() : '';
                const editedA = dk ? (curA === '' ? autoA > 0 : val(curA) !== autoA) : false;
                rows.push(
                  <tr key={`${c.id}|${k}`} className="tree-l2">
                    <td style={{ paddingLeft: 28 }}>
                      <span className="hint" style={{ display: 'inline-block', width: 14 }}>
                        └
                      </span>
                      {COST_STYLE_LABELS[k]}
                    </td>
                    <td className="num hint">{formatYen(unit)}</td>
                    <td className="num">
                      {dk ? (
                        <span className="in-cell">
                          {editedA && resetLink(`自動 ${autoA}`, () => setCompany(c.id, (x) => ({ ...x, appointments: { ...x.appointments, [dk]: autoA > 0 ? String(autoA) : '' } })))}
                          {input(e.appointments[dk], (v) => setCompany(c.id, (x) => ({ ...x, appointments: { ...x.appointments, [dk]: v } })), autoA ? String(autoA) : '0', editedA)}
                        </span>
                      ) : (
                        <span className="hint">出勤に含む</span>
                      )}
                    </td>
                    <td className="num">
                      {dk ? <span className="in-cell">{input(e.delivered[dk], (v) => setCompany(c.id, (x) => ({ ...x, delivered: { ...x.delivered, [dk]: v } })))}</span> : <span className="hint">出勤に含む</span>}
                    </td>
                    <td className="num">
                      <span className="in-cell">
                        {diffL !== null && diffL !== 0 && autoL > 0 && resetLink(`自動 ¥${formatYen(autoL)}`, () => setCompany(c.id, (x) => ({ ...x, labor: { ...x.labor, [k]: String(autoL) } })))}
                        {input(e.labor[k], (v) => setCompany(c.id, (x) => ({ ...x, labor: { ...x.labor, [k]: v } })), autoL ? formatYen(autoL) : '', diffL !== null && diffL !== 0 && autoL > 0)}
                      </span>
                    </td>
                    <td className="num">
                      <span className="in-cell">
                        {diffC !== null && diffC !== 0 && autoC > 0 && resetLink(`自動 ¥${formatYen(autoC)}`, () => setCompany(c.id, (x) => ({ ...x, commute: { ...x.commute, [k]: String(autoC) } })))}
                        {input(e.commute[k], (v) => setCompany(c.id, (x) => ({ ...x, commute: { ...x.commute, [k]: v } })), autoC ? formatYen(autoC) : '', diffC !== null && diffC !== 0 && autoC > 0)}
                      </span>
                    </td>
                  </tr>,
                );
              }
              return rows;
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, background: 'var(--legacy-shade)' }}>
              <td>合計</td>
              <td></td>
              <td className="num">{total.appointments}</td>
              <td className="num">{total.delivered}</td>
              <td className="num">¥{formatYen(total.labor)}</td>
              <td className="num">¥{formatYen(total.commute)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="modal-actions center">
        <button className="btn-green" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? '保存中…' : `保存する(${dateLabel})`}
        </button>
        <button className="btn-line" onClick={requestClose} disabled={busy}>
          閉じる
        </button>
      </div>
      {confirmClose && (
        <Modal onClose={() => setConfirmClose(false)} width="min(480px, 94vw)" showClose={false}>
          <h3>保存されていない変更があります</h3>
          <p style={{ margin: '8px 0 0' }}>このまま閉じると、{dateLabel} に入力した内容は失われます。</p>
          <div className="modal-actions center">
            <button className="btn-green" onClick={() => setConfirmClose(false)}>
              入力に戻る
            </button>
            <button
              className="btn-line danger"
              onClick={() => {
                setConfirmClose(false);
                setDirty(false);
                onClose();
              }}
            >
              保存せずに閉じる
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
